import { useLocale } from "use-intl";
import { AdvertisingConsentSettings } from "@/components/AdvertisingConsentSettings";
const content: Record<ViewerLocale, ReactNode> = {
  en: <>
      <h1>Privacy Policy</h1>
      <p>
        <strong>Last updated: September 25, 2026</strong>
      </p>
      <p>
        Garimpa.Club (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) respects your privacy. This
        Privacy Policy explains how we collect, use, and protect your personal information.
      </p>
      <h2>1. Information We Collect</h2>
      <h3>Account Information</h3>
      <p>When you create an account, we collect:</p>
      <ul>
        <li>
          <strong>Email address</strong> — used for authentication and account recovery
        </li>
        <li>
          <strong>Phone number</strong> — when you sign in or connect with WhatsApp, Telegram, or a
          phone number, used for authentication and messaging
        </li>
        <li>
          <strong>Name and username</strong> — displayed on your public profile
        </li>
        <li>
          <strong>Avatar</strong> — provided by your authentication provider or uploaded by you
        </li>
      </ul>
      <h3>Profile Data</h3>
      <p>
        Your public profile includes your username, display name, avatar, and any public lists you
        create. This information is visible to other users.
      </p>
      <h3>Collection Data</h3>
      <p>
        We store information about your music collection, wishlists, and custom lists. This data is
        associated with your account and may be publicly visible depending on your settings.
      </p>
      <h3>Authentication Data</h3>
      <p>
        We use third-party authentication providers (such as Google and GitHub) to manage sign-in.
        We receive limited profile information from these providers as part of the authentication
        process. We do not store your passwords.
      </p>
      <h3>Usage Analytics</h3>
      <p>
        We use Google Analytics to understand how people use Garimpa.Club and to improve the
        Service. It may collect pages visited, referring pages, device and browser information, and
        bounded interactions with features and shared links.
      </p>
      <p>
        For signed-in users, Google Analytics may receive your stable Garimpa account ID so activity
        can be associated across sessions. We do not send your name, username, email address,
        telephone number, authentication session ID, search text, or form values as Google Analytics
        properties. Customer Match sharing with Google Ads is described in section 3.
      </p>
      <p>
        When you select an offer, Garimpa records a bounded operational click before redirecting you
        to the store. With your consent, we may also send Google Analytics the offer, provider, page
        surface, item type, and Google client and session identifiers. The operational record can
        include your account ID when signed in. We do not send authentication session IDs or visitor
        fingerprints to Google Analytics.
      </p>
      <h3>Cookies</h3>
      <p>We use essential cookies for:</p>
      <ul>
        <li>
          <strong>Authentication</strong> — to keep you signed in
        </li>
        <li>
          <strong>Locale, time zone, and currency preferences</strong> — to keep formatting
          consistent across visits
        </li>
        <li>
          <strong>Login redirects</strong> — to return you to the page you were viewing after
          sign-in
        </li>
      </ul>
      <p>
        Google measurement uses cookieless signals until you allow cookies. If you allow cookies,
        analytics and advertising cookies may be set or read to measure visits, attribute
        conversions, and create remarketing audiences. The embedded YouTube player may set its own
        cookies in accordance with{" "}
        <a href="https://policies.google.com/privacy">Google&apos;s policies</a>.
      </p>
      <AdvertisingConsentSettings />
      <h2>2. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>Provide and maintain the Service</li>
        <li>Display your public profile and lists to other users</li>
        <li>Authenticate your identity</li>
        <li>Send essential communications (account security, service updates)</li>
        <li>Improve the Service</li>
      </ul>
      <h2>3. Information Sharing</h2>
      <p>We do not sell your personal information. We may share data with:</p>
      <ul>
        <li>
          <strong>Service providers</strong> — infrastructure and hosting services necessary to
          operate the platform
        </li>
        <li>
          <strong>Google Ads (Customer Match)</strong> — we share a hashed (SHA-256, one-way)
          version of your verified email address and phone number, plus your country (not hashed),
          so Google can show you more relevant Garimpa ads and measure them. Google uses these
          hashes only to match them to Google accounts and discards those it cannot match. You can
          opt out at any time in <a href="/settings?tab=account">Settings → Account</a>; you are
          removed from the list the next time Google refreshes it. Members in the European Economic
          Area, the United Kingdom, and Switzerland are not included.
        </li>
        <li>
          <strong>Legal requirements</strong> — when required by law or to protect our rights
        </li>
      </ul>
      <h2>4. Third-Party Services</h2>
      <p>Garimpa.Club uses data from:</p>
      <ul>
        <li>
          <strong>
            <a href="https://www.discogs.com">Discogs</a>
          </strong>{" "}
          — music release database (subject to{" "}
          <a href="https://www.discogs.com/settings/terms">Discogs&apos; Terms of Service</a>)
        </li>
        <li>
          <strong>
            <a href="https://fanart.tv">Fanart.tv</a>
          </strong>{" "}
          — artist and album artwork
        </li>
        <li>
          <strong>Google Analytics and advertising services</strong> — aggregate page-view and
          account-creation conversion measurement, Customer Match audiences built from hashed
          contact details (see section 3) and, when you allow cookies, remarketing (subject to{" "}
          <a href="https://policies.google.com/privacy">Google&apos;s Privacy Policy</a>)
        </li>
        <li>
          <strong>YouTube API Services</strong> — embedded video playback (subject to{" "}
          <a href="http://www.google.com/policies/privacy">Google&apos;s Privacy Policy</a>). The
          embedded YouTube player may collect usage data in accordance with Google&apos;s privacy
          practices.
        </li>
      </ul>
      <p>These services have their own privacy policies that govern their data practices.</p>
      <h2>5. Data Retention</h2>
      <p>
        We retain your data for as long as your account is active. When you delete your account:
      </p>
      <ul>
        <li>Your profile, lists, and collection data are permanently deleted</li>
        <li>Some anonymized data may be retained for service improvement</li>
      </ul>
      <h2>6. Data Security</h2>
      <p>
        We implement appropriate security measures to protect your personal information, including
        encrypted connections (HTTPS) and secure authentication practices. However, no method of
        transmission over the Internet is 100% secure.
      </p>
      <h2>7. Your Rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li>
          <strong>Access</strong> your personal data through your account settings
        </li>
        <li>
          <strong>Update</strong> your profile information at any time
        </li>
        <li>
          <strong>Delete</strong> your account and all associated data
        </li>
        <li>
          <strong>Export</strong> your collection and list data
        </li>
      </ul>
      <p>To exercise these rights, visit your account settings or contact us.</p>
      <h2>8. Children&apos;s Privacy</h2>
      <p>
        Garimpa.Club is not directed to children under 13. We do not knowingly collect personal
        information from children. If you believe a child has provided us with personal data, please
        contact us.
      </p>
      <h2>9. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify users of material
        changes by posting a notice on the platform. Continued use of the Service after changes
        constitutes acceptance of the updated policy.
      </p>
      <h2>10. Contact</h2>
      <p>
        If you have questions about this Privacy Policy, please reach out through our{" "}
        <a href="/en/contact">contact form</a>.
      </p>
    </>,
  pt: <>
      <h1>Política de Privacidade</h1>
      <p>
        <strong>Última atualização: 25 de setembro de 2026</strong>
      </p>
      <p>
        O Garimpa.Club (&quot;nós&quot;, &quot;nosso&quot;) respeita sua privacidade. Esta Política
        de Privacidade explica como coletamos, utilizamos e protegemos suas informações pessoais.
      </p>
      <h2>1. Informações que Coletamos</h2>
      <h3>Informações da Conta</h3>
      <p>Ao criar uma conta, coletamos:</p>
      <ul>
        <li>
          <strong>Endereço de e-mail</strong> — utilizado para autenticação e recuperação de conta
        </li>
        <li>
          <strong>Número de telefone</strong> — quando você entra ou se conecta pelo WhatsApp,
          Telegram ou número de telefone, utilizado para autenticação e mensagens
        </li>
        <li>
          <strong>Nome e nome de usuário</strong> — exibidos no seu perfil público
        </li>
        <li>
          <strong>Avatar</strong> — fornecido pelo seu provedor de autenticação ou enviado por você
        </li>
      </ul>
      <h3>Dados do Perfil</h3>
      <p>
        Seu perfil público inclui seu nome de usuário, nome de exibição, avatar e quaisquer listas
        públicas que você criar. Essas informações são visíveis para outros usuários.
      </p>
      <h3>Dados de Coleção</h3>
      <p>
        Armazenamos informações sobre sua coleção musical, listas de desejos e listas
        personalizadas. Esses dados estão associados à sua conta e podem ser publicamente visíveis
        dependendo das suas configurações.
      </p>
      <h3>Dados de Autenticação</h3>
      <p>
        Utilizamos provedores de autenticação terceirizados (como Google e GitHub) para gerenciar o
        login. Recebemos informações limitadas do perfil desses provedores como parte do processo de
        autenticação. Não armazenamos suas senhas.
      </p>
      <h3>Dados de Uso e Analytics</h3>
      <p>
        Utilizamos o Google Analytics para entender como as pessoas usam o Garimpa.Club e melhorar o
        Serviço. Ele pode coletar páginas visitadas, páginas de referência, informações de
        dispositivo e navegador e interações limitadas com recursos e links compartilhados. Para
        usuários autenticados, o Google Analytics pode receber o ID estável da conta para associar
        atividades entre sessões. Não enviamos nome, nome de usuário, e-mail, telefone, ID da sessão
        de autenticação, texto de busca nem valores de formulários como propriedades do Google
        Analytics. O compartilhamento com o Google Ads para Customer Match está descrito na seção 3.
      </p>
      <p>
        Quando você seleciona uma oferta, o Garimpa registra um clique operacional limitado antes de
        redirecionar você para a loja. Com seu consentimento, também podemos enviar ao Google
        Analytics a oferta, o provider, a superfície da página, o tipo de item e os identificadores
        de cliente e sessão do Google. O registro operacional pode incluir o ID da conta quando você
        está autenticado. Não enviamos IDs de sessão de autenticação nem fingerprints de visitantes
        ao Google Analytics.
      </p>
      <h3>Cookies</h3>
      <p>Utilizamos cookies essenciais para:</p>
      <ul>
        <li>
          <strong>Autenticação</strong> — para manter você conectado
        </li>
        <li>
          <strong>Preferências de idioma, fuso horário e moeda</strong> — para manter a formatação
          consistente entre visitas
        </li>
        <li>
          <strong>Redirecionamento após login</strong> — para retornar você à página que estava
          visualizando após entrar
        </li>
      </ul>
      <p>
        Até você permitir cookies, a medição do Google usa sinais sem cookies. Se você permitir
        cookies, cookies de analytics e publicidade poderão ser definidos ou lidos para medir
        visitas, atribuir conversões e criar públicos de remarketing. O player incorporado do
        YouTube pode definir seus próprios cookies de acordo com as{" "}
        <a href="https://policies.google.com/privacy">políticas do Google</a>.
      </p>
      <AdvertisingConsentSettings />
      <h2>2. Como Utilizamos Suas Informações</h2>
      <p>Utilizamos suas informações para:</p>
      <ul>
        <li>Fornecer e manter o Serviço</li>
        <li>Exibir seu perfil público e listas para outros usuários</li>
        <li>Autenticar sua identidade</li>
        <li>Enviar comunicações essenciais (segurança da conta, atualizações do serviço)</li>
        <li>Melhorar o Serviço</li>
      </ul>
      <h2>3. Compartilhamento de Informações</h2>
      <p>Não vendemos suas informações pessoais. Podemos compartilhar dados com:</p>
      <ul>
        <li>
          <strong>Prestadores de serviço</strong> — serviços de infraestrutura e hospedagem
          necessários para operar a plataforma
        </li>
        <li>
          <strong>Google Ads (Customer Match)</strong> — compartilhamos uma versão com hash
          (SHA-256, irreversível) do seu e-mail e telefone verificados, além do seu país (sem hash),
          para que o Google mostre anúncios do Garimpa mais relevantes e meça seus resultados. O
          Google usa esses hashes apenas para associá-los a contas Google e descarta os que não
          consegue associar. Você pode desativar isso a qualquer momento em{" "}
          <a href="/settings?tab=account">Configurações → Conta</a>; você é removido da lista na
          próxima vez que o Google a atualizar. Membros no Espaço Econômico Europeu, no Reino Unido
          e na Suíça não são incluídos.
        </li>
        <li>
          <strong>Requisitos legais</strong> — quando exigido por lei ou para proteger nossos
          direitos
        </li>
      </ul>
      <h2>4. Serviços de Terceiros</h2>
      <p>O Garimpa.Club utiliza dados de:</p>
      <ul>
        <li>
          <strong>
            <a href="https://www.discogs.com">Discogs</a>
          </strong>{" "}
          — banco de dados de lançamentos musicais (sujeito aos{" "}
          <a href="https://www.discogs.com/settings/terms">Termos de Serviço do Discogs</a>)
        </li>
        <li>
          <strong>
            <a href="https://fanart.tv">Fanart.tv</a>
          </strong>{" "}
          — arte de artistas e álbuns
        </li>
        <li>
          <strong>Google Analytics e serviços de publicidade</strong> — medição agregada de
          visualizações de página e da conversão de criação de conta, públicos de Customer Match
          criados a partir de dados de contato com hash (veja a seção 3) e, quando você permite
          cookies, remarketing (sujeito à{" "}
          <a href="https://policies.google.com/privacy">Política de Privacidade do Google</a>)
        </li>
        <li>
          <strong>Serviços de API do YouTube</strong> — reprodução de vídeo incorporado (sujeito à{" "}
          <a href="http://www.google.com/policies/privacy">Política de Privacidade do Google</a>). O
          player incorporado do YouTube pode coletar dados de uso de acordo com as práticas de
          privacidade do Google.
        </li>
      </ul>
      <p>
        Esses serviços possuem suas próprias políticas de privacidade que regem suas práticas de
        dados.
      </p>
      <h2>5. Retenção de Dados</h2>
      <p>Mantemos seus dados enquanto sua conta estiver ativa. Quando você excluir sua conta:</p>
      <ul>
        <li>Seu perfil, listas e dados de coleção são permanentemente excluídos</li>
        <li>Alguns dados anonimizados podem ser mantidos para melhoria do serviço</li>
      </ul>
      <h2>6. Segurança dos Dados</h2>
      <p>
        Implementamos medidas de segurança apropriadas para proteger suas informações pessoais,
        incluindo conexões criptografadas (HTTPS) e práticas seguras de autenticação. No entanto,
        nenhum método de transmissão pela Internet é 100% seguro.
      </p>
      <h2>7. Seus Direitos</h2>
      <p>Você tem o direito de:</p>
      <ul>
        <li>
          <strong>Acessar</strong> seus dados pessoais através das configurações da conta
        </li>
        <li>
          <strong>Atualizar</strong> suas informações de perfil a qualquer momento
        </li>
        <li>
          <strong>Excluir</strong> sua conta e todos os dados associados
        </li>
        <li>
          <strong>Exportar</strong> seus dados de coleção e listas
        </li>
      </ul>
      <p>
        Para exercer esses direitos, acesse as configurações da sua conta ou entre em contato
        conosco.
      </p>
      <h2>8. Privacidade de Crianças</h2>
      <p>
        O Garimpa.Club não é direcionado a crianças menores de 13 anos. Não coletamos
        intencionalmente informações pessoais de crianças. Se você acredita que uma criança nos
        forneceu dados pessoais, entre em contato conosco.
      </p>
      <h2>9. Alterações nesta Política</h2>
      <p>
        Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos os usuários
        sobre alterações significativas por meio de um aviso na plataforma. O uso continuado do
        Serviço após as alterações constitui aceitação da política atualizada.
      </p>
      <h2>10. Contato</h2>
      <p>
        Se você tiver dúvidas sobre esta Política de Privacidade, entre em contato conosco através
        do nosso <a href="/pt/contact">formulário de contato</a>.
      </p>
    </>,
  es: <>
      <h1>Política de Privacidad</h1>
      <p>
        <strong>Última actualización: 25 de septiembre de 2026</strong>
      </p>
      <p>
        Garimpa.Club (&quot;nosotros&quot;, &quot;nuestro&quot;) respeta tu privacidad. Esta
        Política de Privacidad explica cómo recopilamos, utilizamos y protegemos tu información
        personal.
      </p>
      <h2>1. Información que recopilamos</h2>
      <h3>Información de la cuenta</h3>
      <p>Cuando creas una cuenta, recopilamos:</p>
      <ul>
        <li>
          <strong>Dirección de email</strong> — utilizada para la autenticación y la recuperación de
          la cuenta
        </li>
        <li>
          <strong>Número de teléfono</strong> — cuando inicias sesión o te conectas con WhatsApp,
          Telegram o un número de teléfono, utilizado para la autenticación y la mensajería
        </li>
        <li>
          <strong>Nombre y nombre de usuario</strong> — mostrados en tu perfil público
        </li>
        <li>
          <strong>Avatar</strong> — proporcionado por tu proveedor de autenticación o subido por ti
        </li>
      </ul>
      <h3>Datos del perfil</h3>
      <p>
        Tu perfil público incluye tu nombre de usuario, nombre visible, avatar y las listas públicas
        que crees. Esta información es visible para otras personas usuarias.
      </p>
      <h3>Datos de colección</h3>
      <p>
        Almacenamos información sobre tu colección musical, listas de deseos y listas
        personalizadas. Estos datos están asociados a tu cuenta y pueden ser visibles públicamente
        según tu configuración.
      </p>
      <h3>Datos de autenticación</h3>
      <p>
        Utilizamos proveedores de autenticación externos (como Google y GitHub) para gestionar el
        inicio de sesión. Recibimos información limitada del perfil de estos proveedores como parte
        del proceso de autenticación. No almacenamos tus contraseñas.
      </p>
      <h3>Datos de uso y analítica</h3>
      <p>
        Utilizamos Google Analytics para comprender cómo se usa Garimpa.Club y mejorar el Servicio.
        Puede recopilar páginas visitadas, páginas de referencia, información del dispositivo y
        navegador e interacciones limitadas con funciones y enlaces compartidos. Para los usuarios
        que han iniciado sesión, Google Analytics puede recibir el identificador estable de la
        cuenta para asociar actividad entre sesiones. No enviamos nombre, nombre de usuario, correo
        electrónico, teléfono, identificador de sesión de autenticación, texto de búsqueda ni
        valores de formularios como propiedades de Google Analytics. El intercambio con Google Ads
        para Customer Match se describe en la sección 3.
      </p>
      <p>
        Cuando seleccionas una oferta, Garimpa registra un clic operativo limitado antes de
        redirigirte a la tienda. Con tu consentimiento, también podemos enviar a Google Analytics la
        oferta, el proveedor, la superficie de la página, el tipo de elemento y los identificadores
        de cliente y sesión de Google. El registro operativo puede incluir el identificador de tu
        cuenta cuando has iniciado sesión. No enviamos identificadores de sesión de autenticación ni
        huellas de visitantes a Google Analytics.
      </p>
      <h3>Cookies</h3>
      <p>Utilizamos cookies esenciales para:</p>
      <ul>
        <li>
          <strong>Autenticación</strong> — para mantener tu sesión iniciada
        </li>
        <li>
          <strong>Preferencias de idioma, zona horaria y moneda</strong> — para mantener un formato
          coherente entre visitas
        </li>
        <li>
          <strong>Redirecciones de inicio de sesión</strong> — para devolverte a la página que
          estabas viendo después de iniciar sesión
        </li>
      </ul>
      <p>
        Hasta que permitas las cookies, la medición de Google usa señales sin cookies. Si permites
        las cookies, se pueden establecer o leer cookies de analítica y publicidad para medir
        visitas, atribuir conversiones y crear audiencias de remarketing. El reproductor integrado
        de YouTube puede establecer sus propias cookies de acuerdo con las{" "}
        <a href="https://policies.google.com/privacy">políticas de Google</a>.
      </p>
      <AdvertisingConsentSettings />
      <h2>2. Cómo utilizamos tu información</h2>
      <p>Utilizamos tu información para:</p>
      <ul>
        <li>Proporcionar y mantener el Servicio</li>
        <li>Mostrar tu perfil público y tus listas a otras personas usuarias</li>
        <li>Autenticar tu identidad</li>
        <li>
          Enviar comunicaciones esenciales (seguridad de la cuenta y actualizaciones del servicio)
        </li>
        <li>Mejorar el Servicio</li>
      </ul>
      <h2>3. Intercambio de información</h2>
      <p>No vendemos tu información personal. Podemos compartir datos con:</p>
      <ul>
        <li>
          <strong>Proveedores de servicios</strong> — servicios de infraestructura y alojamiento
          necesarios para operar la plataforma
        </li>
        <li>
          <strong>Google Ads (Customer Match)</strong> — compartimos una versión con hash (SHA-256,
          irreversible) de tu email y teléfono verificados, además de tu país (sin hash), para que
          Google te muestre anuncios de Garimpa más relevantes y mida sus resultados. Google usa
          estos hashes solo para asociarlos con cuentas de Google y descarta los que no puede
          asociar. Puedes desactivarlo en cualquier momento en{" "}
          <a href="/settings?tab=account">Configuración → Cuenta</a>; se te quitará de la lista la
          próxima vez que Google la actualice. Los miembros del Espacio Económico Europeo, el Reino
          Unido y Suiza no se incluyen.
        </li>
        <li>
          <strong>Requisitos legales</strong> — cuando lo exija la ley o para proteger nuestros
          derechos
        </li>
      </ul>
      <h2>4. Servicios de terceros</h2>
      <p>Garimpa.Club utiliza datos de:</p>
      <ul>
        <li>
          <strong>
            <a href="https://www.discogs.com">Discogs</a>
          </strong>{" "}
          — base de datos de ediciones musicales (sujeta a los{" "}
          <a href="https://www.discogs.com/settings/terms">Términos de Servicio de Discogs</a>)
        </li>
        <li>
          <strong>
            <a href="https://fanart.tv">Fanart.tv</a>
          </strong>{" "}
          — imágenes de artistas y álbumes
        </li>
        <li>
          <strong>Google Analytics y servicios de publicidad</strong> — medición agregada de vistas
          de página y de la conversión de creación de cuenta, audiencias de Customer Match creadas a
          partir de datos de contacto con hash (consulta la sección 3) y, cuando permites las
          cookies, remarketing (sujeta a la{" "}
          <a href="https://policies.google.com/privacy">Política de Privacidad de Google</a>)
        </li>
        <li>
          <strong>Servicios de API de YouTube</strong> — reproducción de vídeo integrado (sujeta a
          la <a href="http://www.google.com/policies/privacy">Política de Privacidad de Google</a>).
          El reproductor integrado de YouTube puede recopilar datos de uso de acuerdo con las
          prácticas de privacidad de Google.
        </li>
      </ul>
      <p>
        Estos servicios tienen sus propias políticas de privacidad que rigen sus prácticas de datos.
      </p>
      <h2>5. Conservación de datos</h2>
      <p>Conservamos tus datos mientras tu cuenta esté activa. Cuando eliminas tu cuenta:</p>
      <ul>
        <li>Tu perfil, listas y datos de colección se eliminan permanentemente</li>
        <li>Algunos datos anonimizados pueden conservarse para mejorar el servicio</li>
      </ul>
      <h2>6. Seguridad de los datos</h2>
      <p>
        Aplicamos medidas de seguridad adecuadas para proteger tu información personal, incluidas
        conexiones cifradas (HTTPS) y prácticas de autenticación seguras. Sin embargo, ningún método
        de transmisión por Internet es 100 % seguro.
      </p>
      <h2>7. Tus derechos</h2>
      <p>Tienes derecho a:</p>
      <ul>
        <li>
          <strong>Acceder</strong> a tus datos personales desde la configuración de tu cuenta
        </li>
        <li>
          <strong>Actualizar</strong> la información de tu perfil en cualquier momento
        </li>
        <li>
          <strong>Eliminar</strong> tu cuenta y todos los datos asociados
        </li>
        <li>
          <strong>Exportar</strong> los datos de tu colección y tus listas
        </li>
      </ul>
      <p>
        Para ejercer estos derechos, visita la configuración de tu cuenta o ponte en contacto con
        nosotros.
      </p>
      <h2>8. Privacidad infantil</h2>
      <p>
        Garimpa.Club no está dirigido a menores de 13 años. No recopilamos conscientemente
        información personal de menores. Si crees que un menor nos ha proporcionado datos
        personales, ponte en contacto con nosotros.
      </p>
      <h2>9. Cambios en esta política</h2>
      <p>
        Podemos actualizar esta Política de Privacidad periódicamente. Notificaremos los cambios
        importantes mediante un aviso en la plataforma. El uso continuado del Servicio después de
        los cambios constituye la aceptación de la política actualizada.
      </p>
      <h2>10. Contacto</h2>
      <p>
        Si tienes preguntas sobre esta Política de Privacidad, utiliza nuestro{" "}
        <a href="/es/contact">formulario de contacto</a>.
      </p>
    </>
};
function PrivacyPage() {
  const locale = useLocale() as ViewerLocale;
  return content[locale];
}
export { PrivacyPage as component };